<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import TicketTypesAPI from 'dashboard/api/ticketTypes';
import TicketsAPI from 'dashboard/api/tickets';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TicketLifecycleDrawer from './TicketLifecycleDrawer.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const teams = useMapGetter('teams/getTeams');
const agents = useMapGetter('agents/getAgents');

const linkedTickets = ref([]);
const ticketTypes = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const showCreate = ref(false);
const form = ref({});
const attributeValues = ref({});
const selectedTicket = ref(null);

const customerTypes = computed(() =>
  ticketTypes.value.filter(type => type.category === 'customer')
);
const selectedType = computed(() =>
  customerTypes.value.find(type => type.id === form.value.ticket_type_id)
);
const selectedTicketType = computed(() =>
  ticketTypes.value.find(
    type => type.id === selectedTicket.value?.ticket_type.id
  )
);
const attributeDefinitions = computed(
  () => selectedType.value?.attribute_definitions || []
);

const emptyAttributeValue = definition => {
  if (definition.data_type === 'multi_select') return [];
  if (definition.data_type === 'boolean') return false;
  return '';
};

const resetAttributeValues = () => {
  attributeValues.value = Object.fromEntries(
    attributeDefinitions.value.map(definition => [
      definition.id,
      emptyAttributeValue(definition),
    ])
  );
};

const resetForm = () => {
  const senderName = currentChat.value.meta?.sender?.name || '';
  form.value = {
    ticket_type_id: customerTypes.value[0]?.id || '',
    source_conversation_id: props.conversationId,
    title: senderName
      ? t('CUSTOMER_TICKETS.DEFAULT_TITLE', { name: senderName })
      : '',
    description: '',
    priority: currentChat.value.priority || 'normal',
    team_id: currentChat.value.meta?.team?.id || '',
    assignee_id: currentChat.value.meta?.assignee?.id || '',
  };
  resetAttributeValues();
};

watch(() => form.value.ticket_type_id, resetAttributeValues);

const fetchTickets = async () => {
  const { data } = await TicketsAPI.getForConversation(
    props.conversationId,
    'customer'
  );
  linkedTickets.value = data;
};

const fetchTypes = async () => {
  const { data } = await TicketTypesAPI.get();
  ticketTypes.value = data;
};

const openCreateTicket = () => {
  resetForm();
  showCreate.value = true;
};

const closeCreateTicket = () => {
  showCreate.value = false;
};

const openTicket = ticket => {
  selectedTicket.value = ticket;
};

const updateTicket = ticket => {
  const index = linkedTickets.value.findIndex(item => item.id === ticket.id);
  if (index !== -1) linkedTickets.value[index] = ticket;
  selectedTicket.value = ticket;
};

const valueKey = dataType => `${dataType}_value`;

const createTicket = async () => {
  isSaving.value = true;
  const attribute_values = attributeDefinitions.value.map(definition => ({
    ticket_attribute_definition_id: definition.id,
    [valueKey(definition.data_type)]:
      definition.data_type === 'number' &&
      attributeValues.value[definition.id] !== ''
        ? Number(attributeValues.value[definition.id])
        : attributeValues.value[definition.id],
  }));

  try {
    await TicketsAPI.create({
      ticket: { ...form.value, attribute_values },
    });
    await fetchTickets();
    closeCreateTicket();
    useAlert(t('CUSTOMER_TICKETS.CREATE_SUCCESS'));
  } catch (error) {
    useAlert(t('CUSTOMER_TICKETS.CREATE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(async () => {
  isLoading.value = true;
  store.dispatch('teams/get');
  store.dispatch('agents/get');
  try {
    await Promise.all([fetchTickets(), fetchTypes()]);
  } catch (error) {
    useAlert(t('CUSTOMER_TICKETS.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <section class="border-b border-n-weak px-3 py-3">
    <div class="flex items-center justify-between gap-3">
      <div>
        <h3 class="m-0 text-sm font-medium text-n-slate-12">
          {{ $t('CUSTOMER_TICKETS.TITLE') }}
        </h3>
        <p class="mb-0 text-xs text-n-slate-10">
          {{ $t('CUSTOMER_TICKETS.DESCRIPTION') }}
        </p>
      </div>
      <Button
        :label="$t('CUSTOMER_TICKETS.CREATE')"
        icon="i-lucide-plus"
        size="xs"
        variant="ghost"
        color="slate"
        @click="openCreateTicket"
      />
    </div>

    <div
      v-if="linkedTickets.length"
      class="mt-3 divide-y divide-n-weak border-y border-n-weak"
    >
      <button
        v-for="ticket in linkedTickets"
        :key="ticket.id"
        type="button"
        class="flex w-full items-center gap-2 py-2 text-left hover:bg-n-alpha-1"
        @click="openTicket(ticket)"
      >
        <span
          class="flex size-7 shrink-0 items-center justify-center rounded-md bg-n-alpha-2"
        >
          <Icon icon="i-lucide-ticket-check" class="size-3.5 text-n-slate-11" />
        </span>
        <span class="min-w-0 flex-1">
          <span class="block truncate text-sm font-medium text-n-slate-12">
            {{ ticket.title }}
          </span>
          <span class="block truncate text-xs text-n-slate-10">
            {{
              $t('CUSTOMER_TICKETS.STATUS_LINE', {
                state: ticket.ticket_state.name,
              })
            }}
          </span>
        </span>
        <Icon icon="i-lucide-chevron-right" class="size-3.5 text-n-slate-9" />
      </button>
    </div>
    <p v-else-if="!isLoading" class="mb-0 mt-2 text-xs text-n-slate-10">
      {{ $t('CUSTOMER_TICKETS.EMPTY') }}
    </p>
  </section>

  <div
    v-if="showCreate"
    class="fixed inset-0 z-50 flex justify-end bg-n-alpha-4"
    @click.self="closeCreateTicket"
  >
    <form
      class="flex h-full w-full max-w-lg flex-col bg-n-surface-1 shadow-xl"
      @submit.prevent="createTicket"
    >
      <header
        class="flex items-start justify-between gap-4 border-b border-n-weak px-5 py-4"
      >
        <div>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ $t('CUSTOMER_TICKETS.DRAWER_TITLE') }}
          </h2>
          <p class="mb-0 text-sm text-n-slate-10">
            {{ $t('CUSTOMER_TICKETS.DRAWER_DESCRIPTION') }}
          </p>
        </div>
        <Button
          icon="i-lucide-x"
          variant="ghost"
          color="slate"
          type="button"
          @click="closeCreateTicket"
        />
      </header>

      <div class="flex-1 space-y-5 overflow-y-auto px-5 py-5">
        <div
          class="flex items-start gap-3 border-y border-n-weak bg-n-alpha-1 px-1 py-3"
        >
          <Icon icon="i-lucide-eye" class="mt-0.5 size-4 text-n-slate-10" />
          <p class="mb-0 text-sm text-n-slate-11">
            {{ $t('CUSTOMER_TICKETS.VISIBLE_NOTICE') }}
          </p>
        </div>

        <div v-if="!customerTypes.length" class="border-b border-n-weak pb-5">
          <p class="mb-3 text-sm text-n-slate-11">
            {{ $t('CUSTOMER_TICKETS.NO_TYPES') }}
          </p>
          <router-link
            :to="{ name: 'ticket_types_list' }"
            class="text-sm font-medium text-n-blue-11 hover:underline"
          >
            {{ $t('CUSTOMER_TICKETS.CONFIGURE_TYPES') }}
          </router-link>
        </div>

        <template v-else>
          <label class="block space-y-1.5 text-sm text-n-slate-11">
            <span>{{ $t('CUSTOMER_TICKETS.TYPE') }}</span>
            <select
              v-model="form.ticket_type_id"
              required
              class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
            >
              <option
                v-for="type in customerTypes"
                :key="type.id"
                :value="type.id"
              >
                {{ type.name }}
              </option>
            </select>
          </label>

          <label class="block space-y-1.5 text-sm text-n-slate-11">
            <span>{{ $t('CUSTOMER_TICKETS.TICKET_TITLE') }}</span>
            <input
              v-model="form.title"
              required
              maxlength="255"
              class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12 outline-none focus:border-n-brand"
            />
          </label>

          <label class="block space-y-1.5 text-sm text-n-slate-11">
            <span>{{ $t('CUSTOMER_TICKETS.TICKET_DESCRIPTION') }}</span>
            <textarea
              v-model="form.description"
              rows="4"
              maxlength="10000"
              class="w-full resize-none rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-n-slate-12 outline-none focus:border-n-brand"
            />
          </label>

          <section v-if="attributeDefinitions.length" class="space-y-4">
            <div class="border-b border-n-weak pb-2">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('CUSTOMER_TICKETS.DETAILS') }}
              </h3>
              <p class="mb-0 text-xs text-n-slate-10">
                {{ $t('CUSTOMER_TICKETS.DETAILS_DESCRIPTION') }}
              </p>
            </div>

            <label
              v-for="definition in attributeDefinitions"
              :key="definition.id"
              class="block space-y-1.5 text-sm text-n-slate-11"
            >
              <span>
                {{ definition.label }}
                <span v-if="definition.required" class="text-n-ruby-10">
                  {{ $t('CUSTOMER_TICKETS.REQUIRED') }}
                </span>
              </span>
              <input
                v-if="
                  ['text', 'number', 'date', 'datetime'].includes(
                    definition.data_type
                  )
                "
                v-model="attributeValues[definition.id]"
                :type="
                  definition.data_type === 'datetime'
                    ? 'datetime-local'
                    : definition.data_type
                "
                :required="definition.required"
                class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12 outline-none focus:border-n-brand"
              />
              <select
                v-else-if="definition.data_type === 'select'"
                v-model="attributeValues[definition.id]"
                :required="definition.required"
                class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
              >
                <option value="" disabled>
                  {{ $t('CUSTOMER_TICKETS.SELECT_OPTION') }}
                </option>
                <option
                  v-for="option in definition.configuration.options"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </select>
              <select
                v-else-if="definition.data_type === 'multi_select'"
                v-model="attributeValues[definition.id]"
                :required="definition.required"
                multiple
                class="min-h-24 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-n-slate-12"
              >
                <option
                  v-for="option in definition.configuration.options"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </select>
              <span
                v-else-if="definition.data_type === 'boolean'"
                class="flex items-center gap-2"
              >
                <input
                  v-model="attributeValues[definition.id]"
                  type="checkbox"
                  class="size-4"
                />
                {{ $t('CUSTOMER_TICKETS.CONFIRMED') }}
              </span>
            </label>
          </section>

          <div class="grid grid-cols-2 gap-3">
            <label class="space-y-1.5 text-sm text-n-slate-11">
              <span>{{ $t('CUSTOMER_TICKETS.PRIORITY') }}</span>
              <select
                v-model="form.priority"
                class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
              >
                <option value="normal">
                  {{ $t('CUSTOMER_TICKETS.PRIORITIES.NORMAL') }}
                </option>
                <option value="low">
                  {{ $t('CUSTOMER_TICKETS.PRIORITIES.LOW') }}
                </option>
                <option value="high">
                  {{ $t('CUSTOMER_TICKETS.PRIORITIES.HIGH') }}
                </option>
                <option value="urgent">
                  {{ $t('CUSTOMER_TICKETS.PRIORITIES.URGENT') }}
                </option>
              </select>
            </label>
            <label class="space-y-1.5 text-sm text-n-slate-11">
              <span>{{ $t('CUSTOMER_TICKETS.TEAM') }}</span>
              <select
                v-model="form.team_id"
                class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
              >
                <option value="">
                  {{ $t('CUSTOMER_TICKETS.UNASSIGNED') }}
                </option>
                <option v-for="team in teams" :key="team.id" :value="team.id">
                  {{ team.name }}
                </option>
              </select>
            </label>
          </div>

          <label class="block space-y-1.5 text-sm text-n-slate-11">
            <span>{{ $t('CUSTOMER_TICKETS.ASSIGNEE') }}</span>
            <select
              v-model="form.assignee_id"
              class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
            >
              <option value="">{{ $t('CUSTOMER_TICKETS.UNASSIGNED') }}</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
          </label>
        </template>
      </div>

      <footer class="flex justify-end gap-2 border-t border-n-weak px-5 py-4">
        <Button
          :label="$t('CUSTOMER_TICKETS.CANCEL')"
          variant="ghost"
          color="slate"
          type="button"
          @click="closeCreateTicket"
        />
        <Button
          v-if="customerTypes.length"
          :label="$t('CUSTOMER_TICKETS.SUBMIT')"
          :is-loading="isSaving"
          type="submit"
        />
      </footer>
    </form>
  </div>

  <TicketLifecycleDrawer
    v-if="selectedTicket && selectedTicketType"
    :ticket="selectedTicket"
    :ticket-type="selectedTicketType"
    @close="selectedTicket = null"
    @saved="updateTicket"
  />
</template>
